import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Rocket, Crown, Gift, Gamepad2, Truck, CreditCard, 
  MessageCircle, Bell, Shield, Smartphone, TrendingUp,
  Users, Heart, Zap, Star, ChevronRight, Check, X,
  Play, ArrowRight, Sparkles, Target, BarChart3,
  Store, MapPin, Clock, Phone, Mail, ExternalLink,
  Coins, Send, Twitter
} from 'lucide-react';

// ============================================
// 🚀 PAGE DE VENTE SAAS - HEARTBEATS
// "Transformez n'importe quel commerce en lieu de vie"
// ============================================

const MarketingPage = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { value: '847%', label: "d'engagement en plus", icon: TrendingUp },
    { value: '3.2x', label: 'plus de visites/mois', icon: Users },
    { value: '68%', label: 'de rétention client', icon: Heart },
    { value: '15min', label: 'temps moyen sur l\'app', icon: Clock },
  ];

  const features = [
    {
      icon: Heart,
      title: "Heartbeats",
      subtitle: "L'oasis de positivité",
      description: "Vos clients papotent entre voisins, refont le monde et rompent la solitude. Une communauté bienveillante qui fidélise !",
      color: "from-pink-500 to-rose-500",
      metrics: ["+45% de visites", "Zéro swipe, 100% respect"]
    },
    {
      icon: Gamepad2,
      title: "Jeux",
      subtitle: "8 jeux addictifs",
      description: "Roue de la fortune, grattage, snake... Ils reviennent chaque jour pour leur partie gratuite.",
      color: "from-purple-500 to-pink-500",
      metrics: ["4.2 parties/jour/client", "Budget maîtrisé"]
    },
    {
      icon: Crown,
      title: "Fidélité 3.0",
      subtitle: "Adieu les cartes papier",
      description: "Tampons digitaux, couronnes, badges. La fidélité devient un jeu addictif pour vos clients.",
      color: "from-yellow-500 to-orange-500",
      metrics: ["+68% de rétention", "0 carte perdue"]
    },
    {
      icon: Coins,
      title: "Token HBT",
      subtitle: "Récompenses crypto",
      description: "Vos clients gagnent des tokens échangeables contre des réductions ou convertibles en argent.",
      color: "from-amber-500 to-yellow-500",
      metrics: ["30% APY staking", "-5% en HBT"]
    },
  ];

  const testimonials = [
    {
      name: "Mohamed A.",
      role: "Gérant, La Petite Épicerie",
      location: "Vermelles (62)",
      quote: "Mes clients ne viennent plus juste acheter. Ils viennent vivre. Le chiffre a suivi naturellement.",
      avatar: "🧔",
      metrics: "+47% CA en 6 mois"
    },
    {
      name: "Sophie L.",
      role: "Gérante, Night Shop 59",
      location: "Lille (59)",
      quote: "Le module Heartbeats a créé une vraie communauté. Mes clients se connaissent tous maintenant.",
      avatar: "👩",
      metrics: "312 matchs en 3 mois"
    },
    {
      name: "Karim B.",
      role: "Propriétaire, K-Store",
      location: "Lens (62)",
      quote: "Les jeux ont tout changé. Ils reviennent chaque jour pour leur partie gratuite.",
      avatar: "👨",
      metrics: "x3.5 visites/semaine"
    },
  ];

  const pricing = [
    {
      name: "Starter",
      price: "149",
      description: "Pour démarrer en douceur",
      features: [
        "Fidélité digitale",
        "3 jeux inclus",
        "Notifications push",
        "Support email",
        "1 utilisateur admin"
      ],
      notIncluded: ["Module Heartbeats", "Token HBT", "Analytics avancés"],
      cta: "Essai gratuit 14j",
      popular: false
    },
    {
      name: "Business",
      price: "249",
      description: "Le plus populaire",
      features: [
        "Tout Starter +",
        "Module Heartbeats 💜",
        "8 jeux complets",
        "Token HBT intégré",
        "Analytics avancés",
        "Support prioritaire",
        "3 utilisateurs admin"
      ],
      notIncluded: [],
      cta: "Démarrer maintenant",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Sur mesure",
      description: "Multi-sites & personnalisation",
      features: [
        "Tout Business +",
        "Multi-magasins",
        "API complète",
        "White-label possible",
        "Account manager dédié",
        "Formation sur site",
        "SLA garanti"
      ],
      notIncluded: [],
      cta: "Nous contacter",
      popular: false
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    // Ici on enverrait l'email à l'API
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0519] via-[#1a0a2e] to-[#0f0519] text-white overflow-x-hidden">
      
      {/* Floating Hearts Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute text-2xl opacity-20 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          >
            💜
          </div>
        ))}
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f0519]/90 backdrop-blur-lg border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/2a092d8c-83c7-4c9e-ac69-8947dad50218/images/0b5e9362a5bf157caeeee00c101dc4b566cce930b468c990a9f498a5fbd16474.png" 
                alt="Heartbeats" 
                className="h-10 w-10"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-pink-500 bg-clip-text text-transparent">
                Heartbeats
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-300 hover:text-pink-400 transition-colors">Fonctionnalités</a>
              <a href="#testimonials" className="text-gray-300 hover:text-pink-400 transition-colors">Témoignages</a>
              <a href="#pricing" className="text-gray-300 hover:text-pink-400 transition-colors">Tarifs</a>
              <a href="#contact" className="text-gray-300 hover:text-pink-400 transition-colors">Contact</a>
            </div>

            <a 
              href="#contact"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 px-6 py-2 rounded-full font-semibold transition-all hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
            >
              Démarrer
            </a>
          </div>
        </div>
      </nav>

      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-x-hidden overflow-y-auto">
        {/* Animated orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          {/* Logo */}
          <motion.img 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            src="https://static.prod-images.emergentagent.com/jobs/2a092d8c-83c7-4c9e-ac69-8947dad50218/images/15c716ddf071f0492cfb0c29744551bb7fc6c473dc08cd8a27826fc4707dcd7e.png" 
            alt="Heartbeats Logo" 
            className="w-64 md:w-80 mx-auto mb-6"
          />

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-purple-500/30 rounded-full px-4 py-2 mb-8"
          >
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-sm">Plateforme SaaS pour commerçants de proximité</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight"
          >
            <span className="text-white">Votre commerce.</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">
              Leur lieu de vie.
            </span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-yellow-400 font-serif italic mb-6"
          >
            ✨ Et mon cœur bat plus fort ✨
          </motion.p>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-12"
          >
            Fidélité. Rencontres. Jeux. Crypto.
            <br />
            <span className="text-white font-medium">Une app. Zéro friction. +847% d'engagement.</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <a 
              href="#contact"
              className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-bold text-lg overflow-hidden transition-transform hover:scale-105 hover:shadow-xl hover:shadow-purple-500/30"
            >
              <span className="relative z-10 flex items-center gap-2">
                <Rocket className="w-5 h-5" />
                Essai gratuit 14 jours
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
            
            <button 
              onClick={() => setIsVideoPlaying(true)}
              className="flex items-center gap-3 px-8 py-4 border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black rounded-full font-bold transition-all"
            >
              <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <Play className="w-4 h-4 ml-0.5" />
              </div>
              Voir la démo (2min)
            </button>
          </motion.div>

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
          >
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-yellow-400 to-pink-400 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-purple-500/40 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-purple-400 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ============================================ */}
      {/* PROBLEM SECTION */}
      {/* ============================================ */}
      <section className="py-24 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 font-serif">
              Le problème que vous connaissez
              <span className="text-gray-500"> (trop bien)</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                emoji: "😴", 
                title: "Clients fantômes", 
                desc: "Ils viennent une fois. Disparaissent. Vous les revoyez jamais." 
              },
              { 
                emoji: "📉", 
                title: "Concurrence Amazon", 
                desc: "Pourquoi venir chez vous quand tout est livré en 24h ?" 
              },
              { 
                emoji: "🗑️", 
                title: "Fidélité jetable", 
                desc: "Cartes papier perdues. Tampons oubliés. Engagement = 0." 
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8 text-center"
              >
                <div className="text-5xl mb-4">{item.emoji}</div>
                <h3 className="text-xl font-bold mb-2 text-red-400">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="text-2xl text-gray-400">
              Et si vos clients avaient une <span className="text-white font-bold">vraie raison</span> de revenir ?
            </p>
            <p className="text-2xl mt-2">
              Pas une promo. Pas un coupon. 
              <span className="bg-gradient-to-r from-yellow-400 to-pink-400 bg-clip-text text-transparent font-bold"> Une communauté.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FEATURES SHOWCASE */}
      {/* ============================================ */}
      <section id="features" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 font-serif">
              4 modules. 
              <span className="bg-gradient-to-r from-yellow-400 to-pink-400 bg-clip-text text-transparent"> 1 révolution.</span>
            </h2>
            <p className="text-xl text-gray-400">
              Chaque module est conçu pour ramener vos clients. Encore. Et encore.
            </p>
          </motion.div>

          {/* Feature tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {features.map((feature, i) => (
              <button
                key={i}
                onClick={() => setActiveFeature(i)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                  activeFeature === i 
                    ? `bg-gradient-to-r ${feature.color} text-white` 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                <feature.icon className="w-5 h-5" />
                {feature.title}
              </button>
            ))}
          </div>

          {/* Active feature display */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFeature}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-3xl p-8 md:p-12"
            >
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${features[activeFeature].color} mb-6`}>
                    {React.createElement(features[activeFeature].icon, { className: "w-8 h-8" })}
                  </div>
                  <h3 className="text-3xl font-bold mb-2 text-yellow-400">{features[activeFeature].title}</h3>
                  <p className="text-xl text-gray-300 mb-4">{features[activeFeature].subtitle}</p>
                  <p className="text-lg text-gray-400 mb-8">{features[activeFeature].description}</p>
                  
                  <div className="flex flex-wrap gap-4">
                    {features[activeFeature].metrics.map((metric, i) => (
                      <div key={i} className="bg-white/5 border border-purple-500/20 rounded-full px-4 py-2 text-sm">
                        <span className="text-green-400">✓</span> {metric}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="relative">
                  {/* Phone mockup */}
                  <div className="relative mx-auto w-64 h-[500px] bg-gradient-to-br from-purple-900 to-pink-900 rounded-[3rem] border-4 border-purple-500/50 overflow-hidden shadow-2xl shadow-purple-500/20">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl" />
                    <div className={`w-full h-full bg-gradient-to-br ${features[activeFeature].color} opacity-20`} />
                    <div className="absolute inset-4 top-8 bg-black/50 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                      {React.createElement(features[activeFeature].icon, { className: "w-24 h-24 text-white/30" })}
                    </div>
                  </div>
                  
                  {/* Floating elements */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-black font-bold px-4 py-2 rounded-full text-sm shadow-lg"
                  >
                    +47% CA
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ============================================ */}
      {/* SOCIAL PROOF - TESTIMONIALS */}
      {/* ============================================ */}
      <section id="testimonials" className="py-24 bg-gradient-to-b from-transparent via-pink-900/10 to-transparent">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 font-serif">
              Ils l'ont fait.
              <span className="text-gray-500"> Les chiffres parlent.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-2xl p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-4xl">{testimonial.avatar}</div>
                  <div>
                    <div className="font-bold text-yellow-400">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {testimonial.location}
                    </div>
                  </div>
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.quote}"</p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-full px-4 py-2 text-green-400 text-sm font-medium inline-block">
                  {testimonial.metrics}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRICING */}
      {/* ============================================ */}
      <section id="pricing" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 font-serif">
              Investissement.
              <span className="text-gray-500"> Pas dépense.</span>
            </h2>
            <p className="text-xl text-gray-400">
              ROI moyen : 340% sur 6 mois. Garanti ou remboursé.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricing.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative rounded-2xl p-8 ${
                  plan.popular 
                    ? 'bg-gradient-to-br from-purple-600/30 to-pink-600/30 border-2 border-pink-500' 
                    : 'bg-purple-900/20 border border-purple-500/30'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-1 rounded-full text-sm font-bold">
                    Le plus populaire
                  </div>
                )}
                
                <h3 className="text-2xl font-bold mb-2 text-yellow-400">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.description}</p>
                
                <div className="mb-6">
                  {plan.price === "Sur mesure" ? (
                    <div className="text-3xl font-bold">{plan.price}</div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-black">{plan.price}</span>
                      <span className="text-gray-400">€/mois</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-500">
                      <X className="w-5 h-5 text-gray-600 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a 
                  href="#contact"
                  className={`block w-full py-4 rounded-full font-bold text-center transition-all ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400' 
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {plan.cta}
                </a>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-8">
            Tous les prix sont HT. Sans engagement. Annulez quand vous voulez.
          </p>
        </div>
      </section>

      {/* ============================================ */}
      {/* FINAL CTA */}
      {/* ============================================ */}
      <section id="contact" className="py-24 bg-gradient-to-r from-purple-900 via-pink-900 to-purple-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6 font-serif">
              Prêt à transformer
              <br />
              <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-400 bg-clip-text text-transparent">
                votre commerce ?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-12">
              14 jours d'essai. Zéro carte bancaire. Zéro risque.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="flex-1 px-6 py-4 bg-white/10 border border-purple-500/30 rounded-full text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
                  required
                />
                <button 
                  type="submit"
                  className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black rounded-full font-bold hover:from-yellow-400 hover:to-orange-400 transition-all hover:scale-105"
                >
                  Démarrer →
                </button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-500/20 border border-green-500 rounded-2xl p-8 max-w-lg mx-auto"
              >
                <div className="text-4xl mb-4">💜</div>
                <h3 className="text-2xl font-bold mb-2">C'est parti !</h3>
                <p className="text-gray-300">
                  On vous envoie tout par email dans les 2 prochaines minutes.
                </p>
              </motion.div>
            )}

            <p className="text-sm text-gray-400 mt-6">
              Déjà 47 commerces nous font confiance dans les Hauts-de-France
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <footer className="py-16 px-4 bg-[#0f0519] border-t border-purple-500/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/2a092d8c-83c7-4c9e-ac69-8947dad50218/images/15c716ddf071f0492cfb0c29744551bb7fc6c473dc08cd8a27826fc4707dcd7e.png" 
                  alt="Heartbeats" 
                  className="h-12"
                />
              </Link>
              <p className="text-gray-400 mb-6">
                Heartbeats révolutionne le commerce de proximité. L'amour est au coin de la rue !
              </p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 rounded-full bg-purple-900/50 hover:bg-pink-600 flex items-center justify-center transition-colors">
                  <Send className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-purple-900/50 hover:bg-pink-600 flex items-center justify-center transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-purple-900/50 hover:bg-pink-600 flex items-center justify-center transition-colors">
                  <MessageCircle className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-yellow-400 mb-4">Produit</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-pink-400 transition-colors">Fonctionnalités</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-pink-400 transition-colors">Tarifs</a></li>
                <li><a href="#" className="text-gray-400 hover:text-pink-400 transition-colors">Démo</a></li>
                <li><a href="#" className="text-gray-400 hover:text-pink-400 transition-colors">Roadmap</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-yellow-400 mb-4">Entreprise</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-pink-400 transition-colors">À propos</a></li>
                <li><a href="#" className="text-gray-400 hover:text-pink-400 transition-colors">White Paper</a></li>
                <li><a href="#" className="text-gray-400 hover:text-pink-400 transition-colors">Investisseurs</a></li>
                <li><a href="#" className="text-gray-400 hover:text-pink-400 transition-colors">Carrières</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-yellow-400 mb-4">Contact</h4>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:contact@heartbeats.fr" className="text-gray-400 hover:text-pink-400 transition-colors flex items-center gap-2">
                    <Mail className="w-4 h-4" /> contact@heartbeats.fr
                  </a>
                </li>
                <li>
                  <a href="mailto:invest@heartbeats.fr" className="text-gray-400 hover:text-pink-400 transition-colors flex items-center gap-2">
                    <Mail className="w-4 h-4" /> invest@heartbeats.fr
                  </a>
                </li>
                <li>
                  <a href="tel:+33780648976" className="text-gray-400 hover:text-pink-400 transition-colors flex items-center gap-2">
                    <Phone className="w-4 h-4" /> 07 80 64 89 76
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-purple-500/20 pt-8 text-center">
            <p className="text-gray-500 mb-2">
              © 2025 Heartbeats. Tous droits réservés. | <a href="#" className="text-pink-400 hover:underline">Mentions légales</a> | <a href="#" className="text-pink-400 hover:underline">CGV</a>
            </p>
            <p className="text-gray-400 italic">
              💜 Et mon cœur bat plus fort 💜
            </p>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoPlaying && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setIsVideoPlaying(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-purple-900/50 border border-purple-500/30 rounded-2xl p-2 max-w-4xl w-full aspect-video flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <Play className="w-16 h-16 mx-auto mb-4 text-purple-400" />
                <p className="text-gray-400">Vidéo de démo à intégrer</p>
              </div>
            </motion.div>
            <button 
              onClick={() => setIsVideoPlaying(false)}
              className="absolute top-6 right-6 text-white/50 hover:text-white"
            >
              <X className="w-8 h-8" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MarketingPage;
